
const MAX_TEXT = 280;

const safeString = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
};

const truncate = (value, limit = MAX_TEXT) => {
  const text = safeString(value).replace(/\s+/g, ' ').trim();
  if (!text) return '';
  return text.length <= limit ? text : `${text.slice(0, limit - 1).trim()}…`;
};

const humanize = (value) => safeString(value)
  .replace(/_/g, ' ')
  .replace(/\s+/g, ' ')
  .replace(/\b\w/g, (char) => char.toUpperCase())
  .trim();

const normaliseArray = (value) => Array.isArray(value) ? value.filter(Boolean) : [];

const sectionSummary = (value) => {
  if (typeof value === 'string') return truncate(value, 220);
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return truncate(value.map((item) => safeString(item)).filter(Boolean).join(' · '), 220);
  if (value && typeof value === 'object') {
    const preferred = ['summary', 'headline', 'title', 'label', 'evidence', 'interpretation', 'description', 'action', 'why_now', 'expected_benefit', 'rationale', 'outcome'];
    for (const key of preferred) {
      if (safeString(value[key])) return truncate(value[key], 220);
    }
    const parts = Object.entries(value)
      .filter(([, entry]) => typeof entry === 'string' || typeof entry === 'number' || typeof entry === 'boolean')
      .slice(0, 3)
      .map(([key, entry]) => `${humanize(key)}: ${safeString(entry)}`);
    return truncate(parts.join(' · '), 220);
  }
  return '';
};

const buildEvidenceObject = ({ section, index, title, summary, raw }) => ({
  id: `ev_${section}_${index + 1}`,
  section,
  title: truncate(title || humanize(section), 120),
  summary: truncate(summary || sectionSummary(raw), 240),
  source: humanize(section),
  raw,
});

const flattenSection = (sectionName, value) => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value
      .filter(Boolean)
      .map((item, index) => {
        const title = item?.title || item?.theme || item?.headline || item?.label || item?.path_name || item?.outcome || `${humanize(sectionName)} ${index + 1}`;
        return buildEvidenceObject({ section: sectionName, index, title, summary: sectionSummary(item), raw: item });
      });
  }
  if (typeof value === 'object') {
    const nestedListKey = Object.keys(value).find((key) => Array.isArray(value[key]) && value[key].length);
    if (nestedListKey) {
      return flattenSection(`${sectionName}_${nestedListKey}`, value[nestedListKey]);
    }
    return [buildEvidenceObject({ section: sectionName, index: 0, title: value?.headline || value?.title || humanize(sectionName), summary: sectionSummary(value), raw: value })];
  }
  return [buildEvidenceObject({ section: sectionName, index: 0, title: humanize(sectionName), summary: sectionSummary(value), raw: value })];
};

const selectSnapshot = (result = {}) => {
  const summaryKeys = ['executive_summary', 'report_summary', 'summary', 'overall_health', 'executive_position', 'future_state_summary', 'automation_blueprint_summary', 'implementation_roadmap_summary'];
  const snapshot = [];
  summaryKeys.forEach((key) => {
    if (!result[key]) return;
    const text = sectionSummary(result[key]);
    if (text) snapshot.push({ label: humanize(key), text });
  });
  return snapshot.slice(0, 5);
};

export const classifyConsultantQuestion = (question = '') => {
  const q = question.toLowerCase();
  if (/evidence|prove|support|backing|why do you say/.test(q)) return 'evidence_request';
  if (/fix first|priority|prioritise|prioritize|biggest|highest roi|what should we do first/.test(q)) return 'prioritization';
  if (/action plan|roadmap|implement|implementation/.test(q)) return 'action_plan';
  if (/executive|leadership|board|summary|summarise|summarize/.test(q)) return 'executive_summary';
  if (/compare|versus|vs\b/.test(q)) return 'comparison';
  if (/why|explain|how come|what is driving/.test(q)) return 'diagnostic_explanation';
  return 'diagnostic_explanation';
};

export const buildConsultantQaContext = ({ tabId, tabLabel, resultData, modelTitle, processLabel, entityLabel }) => {
  const result = resultData && typeof resultData === 'object' ? resultData : {};
  const entries = Object.entries(result);
  const evidence = entries.flatMap(([sectionName, value]) => flattenSection(sectionName, value));

  const rankedEvidence = evidence
    .filter((item) => item.summary)
    .sort((a, b) => (b.summary.length + b.title.length) - (a.summary.length + a.title.length));

  return {
    scope: {
      tabId,
      tabLabel,
      modelTitle: modelTitle || '',
      processLabel: processLabel || 'Process',
      entityLabel: entityLabel || 'Work Item',
    },
    snapshot: selectSnapshot(result),
    evidence: rankedEvidence.slice(0, 18),
    totalEvidence: rankedEvidence.length,
  };
};

const filterEvidenceForQuestion = ({ questionType, question, context }) => {
  const lowerQuestion = (question || '').toLowerCase();
  const scored = context.evidence.map((item) => {
    let score = 0;
    const haystack = `${item.title} ${item.summary} ${item.section}`.toLowerCase();
    lowerQuestion.split(/[^a-z0-9]+/).filter((token) => token.length > 3).forEach((token) => {
      if (haystack.includes(token)) score += 12;
    });
    if (/recommend|priority|action|owner|benefit/.test(haystack) && ['prioritization', 'action_plan'].includes(questionType)) score += 30;
    if (/evidence|discoveryRule|signal|theme|health|summary|rationale/.test(haystack) && questionType === 'diagnostic_explanation') score += 18;
    if (/summary|headline|position|health/.test(haystack) && questionType === 'executive_summary') score += 24;
    if (/evidence|signal|discoveryRule|theme/.test(haystack) && questionType === 'evidence_request') score += 28;
    return { ...item, score };
  });

  return scored.sort((a, b) => b.score - a.score).slice(0, 8).map(({ raw, score, ...rest }) => ({ ...rest }));
};

export const buildConsultantQaPrompt = ({ question, questionType, context, priorTurns = [] }) => {
  const selectedEvidence = filterEvidenceForQuestion({ questionType, question, context });
  const conversationSummary = normaliseArray(priorTurns).slice(-3).map((turn) => ({
    question: truncate(turn?.question, 120),
    summary: truncate(turn?.answerSummary, 180),
  }));

  const promptPayload = {
    scope: context.scope,
    snapshot: context.snapshot,
    selected_evidence: selectedEvidence,
    prior_turns: conversationSummary,
    answer_constraints: {
      grounded_only: true,
      max_key_points: 4,
      max_recommended_actions: 3,
      must_reference_evidence_ids: true,
      must_state_limits: true,
    },
  };

  return `You are an AI process intelligence consultant answering questions about a single generated consultant tab.

BOUNDARY RULES
- Answer only from the supplied tab context, snapshot, and evidence objects.
- Do not invent facts, benchmarks, hidden data, calculations, or root causes.
- Stay within the current tab scope. This is not a general chatbot.
- If the answer cannot be supported from the supplied evidence, say so clearly.
- Distinguish observed evidence from interpretation and from recommended action.
- Keep the answer concise, business-oriented, and directly useful.

QUESTION TYPE
${questionType}

RESPONSE FORMAT
Return exactly one JSON object with this structure:
{
  "answer_summary": "",
  "key_points": [
    { "label": "", "detail": "", "evidence_ids": ["ev_x"] }
  ],
  "recommended_actions": [
    { "action": "", "why": "", "priority": "Now|Next|Later", "evidence_ids": ["ev_x"] }
  ],
  "limits": [""],
  "follow_up_questions": ["", ""],
  "evidence_used": ["ev_x"]
}

USER QUESTION
${question}

TAB CONTEXT
${JSON.stringify(promptPayload, null, 2)}`;
};

export const getSuggestedQuestionsForTab = (tabId) => {
  const shared = [
    'What is the biggest issue here?',
    'What should we fix first?',
    'Show the strongest evidence behind this.',
  ];
  const map = {
    executive: [
      'Why is the process health scored this way?',
      'Summarise this for leadership.',
      'Which issues look most material?',
    ],
    improvements: [
      'Which improvement should we prioritise first?',
      'Which opportunities look highest value?',
      'Show the evidence behind the top opportunity.',
    ],
    automation: [
      'Which automation should we implement first?',
      'Which automation looks lowest effort and highest impact?',
      'What evidence supports this automation direction?',
    ],
    workflow: [
      'Which redesign change matters most?',
      'What problem is the redesign trying to solve?',
      'Show the evidence behind the future-state recommendation.',
    ],
    roadmap: [
      'What should happen in phase one?',
      'What are the main delivery risks?',
      'How should leadership sequence this roadmap?',
    ],
    report: [
      'Give me the shortest possible executive summary.',
      'What should we fix first across the whole report?',
      'Which recommendation has the strongest evidence?',
    ],
  };
  return [...(map[tabId] || []), ...shared].slice(0, 5);
};
